import { useEffect, useState } from "react";
import {
  DyteAudioVisualizer,
  DyteAvatar,
  DyteCameraToggle,
  DyteClock,
  DyteDialogManager,
  DyteLeaveButton,
  DyteMicToggle,
  DyteNameTag,
  DyteParticipantCount,
  DyteParticipantsAudio,
  DyteParticipantTile,
} from "@dytesdk/react-ui-kit";
import { provideDyteDesignSystem, Transcript, UIConfig } from "@dytesdk/ui-kit";
import type DyteClient from "@dytesdk/web-core";
import { DyteParticipant } from "@dytesdk/web-core";
import { Lightbulb, ListChecks } from "lucide-react";
import { useNavigate } from "react-router-dom";

import { useMeeting, useUpsertMeeting } from "../../../../../hooks/useMeeting";
import { useLiveMeetingAnalysis } from "../../../../../hooks/useMeetingAnalysis";
import { useUpsertMeetingTranscript } from "../../../../../hooks/useMeetingTranscript";
import { Resource } from "../../../../../types";
import { CustomStates, SetStates } from "../../../../../types/dyte";
import { SessionTranscripts } from "../../../../../types/supabase";
import AlertSection from "../components/AlertSection";
import ChoicesSection from "../components/ChoicesSection";
import Resources from "../components/Resources";

function RemoteView({
  meeting,
  config,
  states,
  setStates,
  sessionId,
  meetingTranscript,
}: {
  meeting: DyteClient;
  config: UIConfig;
  states: CustomStates;
  setStates: SetStates;
  sessionId: string;
  conversationPrompts: string[];
  meetingTranscript: SessionTranscripts;
}) {
  const navigate = useNavigate();

  const localUser = meeting.self;

  const { data: meetingSupa } = useMeeting(sessionId);
  const { data: meetingAnalysis } = useLiveMeetingAnalysis(sessionId);
  const { mutate: upsertTranscript } = useUpsertMeetingTranscript();
  const { mutate: upsertMeeting } = useUpsertMeeting();
  const [remoteParticipant, setRemoteParticipant] =
    useState<DyteParticipant | null>(null);

  console.log("MEETING SUPA: ", meetingSupa);
  console.log("MEETING ANALYSIS: ", meetingAnalysis);
  console.log("MEETING TRANSCRIPT: ", meetingTranscript);

  const [resources, setResources] = useState<Resource[]>([]);

  const speakerMap = new Map<string, string>();
  speakerMap.set(localUser.name, "MENTOR");

  const handleResourceToggle = (resource: string) => {
    setResources(
      resources.map((r) => {
        if (r.name === resource) {
          r.checked = !r.checked;
        }
        return r;
      })
    );
  };

  useEffect(() => {
    const activeParticipants = meeting.participants.active.toArray();
    const initialRemoteParticipant = activeParticipants[0];
    if (initialRemoteParticipant) {
      setRemoteParticipant(initialRemoteParticipant);
      speakerMap.set(initialRemoteParticipant.name, "MENTEE");
    }

    const handleParticipantJoined = (participant: DyteParticipant) => {
      console.log("Participant joined: ", participant);
      setRemoteParticipant(participant);
      speakerMap.set(participant.name, "MENTEE");
    };

    const handleParticipantLeft = (participant: DyteParticipant) => {
      console.log("Participant left: ", participant);
      setRemoteParticipant(null);
    };

    meeting.participants.joined.on(
      "participantJoined",
      handleParticipantJoined
    );
    meeting.participants.active.on("participantLeft", handleParticipantLeft);

    return () => {
      meeting.participants.active.removeListener(
        "participantJoined",
        handleParticipantJoined
      );
      meeting.participants.active.removeListener(
        "participantLeft",
        handleParticipantLeft
      );

      meeting.leaveRoom();
    };
  }, [meeting]);

  useEffect(() => {
    const handleTranscript = async (transcript: Transcript) => {
      // @ts-ignore
      if (!transcript.isPartialTranscript) {
        console.log(
          "Session ID: ",
          sessionId,
          "Transcript: ",
          transcript.transcript,
          transcript.name
        );

        meetingTranscript.content.push({
          transcript: transcript.transcript,
          speaker: speakerMap.get(transcript.name) || "MENTOR",
          timestamp: transcript.date.toISOString(),
        });

        upsertTranscript(meetingTranscript);
      }
    };

    meeting.ai.on("transcript", handleTranscript);

    return () => {
      meeting.ai.removeListener("transcript", handleTranscript);
    };
  }, []);

  return (
    <div className="flex h-[calc(100vh-64px)]">
      <div className="w-4/6 h-full">
        <div
          className="flex h-full flex-col gap-4"
          ref={(el) => {
            if (!el) return;

            provideDyteDesignSystem(el, {
              theme: "light",
            });

            el?.addEventListener("dyteStateUpdate", (e) => {
              const { detail: newStateUpdate } = e as unknown as {
                detail: CustomStates;
              };
              setStates((oldState: CustomStates) => {
                return {
                  ...oldState,
                  ...newStateUpdate,
                };
              });
            });
          }}
        >
          <div className="relative flex items-center justify-center">
            <div className="absolute left-0 top-0 flex items-center gap-2">
              <DyteClock meeting={meeting} className="h-auto text-vincer-900" />
              <DyteParticipantCount
                meeting={meeting}
                className="h-auto text-vincer-900"
              />
            </div>

            <div className="flex flex-col items-center justify-center rounded-xl bg-vincer-700/90">
              <DyteParticipantTile
                participant={localUser}
                meeting={meeting}
                style={{
                  borderRadius: "12px",
                  width: "213px",
                  height: "120px",
                  aspectRatio: "16/9",
                  backgroundColor: "transparent",
                }}
              >
                <DyteNameTag participant={localUser} meeting={meeting}>
                  <DyteAudioVisualizer participant={localUser} />
                </DyteNameTag>
                <DyteAvatar size="sm" participant={localUser} />
              </DyteParticipantTile>
            </div>
          </div>
          <div className="min-h-0 flex-1">
            <div className="flex h-full items-center justify-start">
              {remoteParticipant ? (
                <div className="size-full rounded-xl border border-vincer-100 bg-vincer-100/50">
                  <DyteParticipantTile
                    participant={remoteParticipant}
                    meeting={meeting}
                    style={{
                      width: "100%",
                      height: "100%",
                      backgroundColor: "transparent",
                      borderRadius: "12px",
                    }}
                  >
                    <DyteNameTag
                      participant={remoteParticipant}
                      meeting={meeting}
                    >
                      <DyteAudioVisualizer participant={remoteParticipant} />
                    </DyteNameTag>
                    <DyteAvatar size="sm" participant={remoteParticipant} />
                  </DyteParticipantTile>
                </div>
              ) : (
                <div className="flex size-full flex-col items-center justify-center rounded-xl bg-vincer-100/50">
                  <p className="font-medium text-vincer-900">
                    Mentee is joining...
                  </p>
                </div>
              )}
            </div>
          </div>
          <div className="flex items-center justify-center space-x-2">
            <DyteMicToggle size="md" meeting={meeting} variant="horizontal" />
            <DyteCameraToggle
              size="md"
              meeting={meeting}
              variant="horizontal"
            />
            <DyteLeaveButton
              size="md"
              variant="horizontal"
              onClick={async () => {
                console.log("ENDING SESSION, SESSION ID: ", sessionId);
                await upsertMeeting({
                  uuid: sessionId,
                  status: "ended",
                });

                await meeting.leave().then(() => {
                  console.log("LEAVING MEETING, SESSION ID: ", sessionId);
                  navigate(`/mentor/post-session?sessionId=${sessionId}`);
                });
              }}
            />
          </div>
        </div>
      </div>
      <div className="w-2/6 h-full pl-4">
        <div className="flex h-full flex-col gap-4">
          <AlertSection alert={meetingAnalysis?.content?.alert || ""} />

          <ChoicesSection
            choices={meetingAnalysis?.content?.mentor_suggestions || []}
            title="Key Topics"
            defaultText="Key topics will appear here..."
            icon={ListChecks}
          />

          <ChoicesSection
            choices={meetingAnalysis?.content?.key_topics || []}
            title="Suggestions"
            defaultText="Suggestions will appear here..."
            icon={Lightbulb}
          />

          <div className="flex justify-start items-center gap-10">
            <Resources
              resources={resources}
              onResourceToggle={handleResourceToggle}
            />
          </div>
        </div>
      </div>

      <DyteParticipantsAudio meeting={meeting} />
      <DyteDialogManager meeting={meeting} config={config} states={states} />
    </div>
  );
}

export default RemoteView;
